import { License } from "@/types/enums";
import {
  calcZoom,
  DEFAULT_AZIMUTH_ANGLE,
  DEFAULT_POLAR_ANGLE,
  DRAGGING_SMOOTH_TIME,
  GAIA_CAMERA_DISTANCE,
  GAIA_FOV,
  GAIA_ZOOM_CONSTANT,
  HENGE_ORTHOGRAPHIC_ZOOM_CONSTANT,
  SMOOTH_TIME,
  VAULT_OBJECT_THUMBNAIL_ZOOM_CONSTANT,
} from "@/utils/cameraUtil";
import { HENGE_SCALE_CONSTANT } from "@/utils/hengeUtil";
import { HengeCameraControls, HngVector3 } from "@/utils/threeUtil";
import { Size } from "@react-three/fiber";
import CameraControlsStdlib from "camera-controls";
import * as THREE from "three";
import FilterPreset from "@/utils/filterPreset";
import { VaultObjectFilter } from "@/types/vault-object-filter-types";
import { VaultObjectAnnotationDto } from "@/types/data-types";
import sanitizer from "sanitize-filename";

export const THUMBNAIL_VERSION = "241217";

export function nameVaultObject(name: string): string {
  return sanitizer(name)
    .replace(/\./g, "_")
    .replace(/\+/g, "-")
    .replace(/=/g, "_")
    .replace(/\//g, "~")
    .replace(/[?#%&]+/g, "-");
}

export const VAULT_OBJECT_THUMBNAIL_TOP_PERCENT = 80;
export const MAX_TAGS = 5;
export const POPOUT_LAYOUT_PANEL_MIN_WIDTH = 240;
export const POPOUT_LAYOUT_PANEL_MAX_WIDTH = 300;
export type RawFileData = {
  uploadType: "raw";
  name: string;
  extension: string;
  file: File;
  url: string;
};
export interface CaptureVaultObjectSizeConfig {
  maxSize: number;
  maxSizeWeight: number;
  currentSizeWeight: number;
}
export const CAPTURE_VAULT_OBJECT_SCALE_WEIGHT_STEP = 0.2;

export interface VaultObjectUploadData {
  uploadType: "raw" | "preprocessed" | "inventory";
  // uid: string; // "preprocessed" : preprocessed uid | "inventory" : original vault object uid
  file: File;
  extension: string;
  url: string;
  // metadata
  name: string;
  tags: string[];
  downloadable: boolean;
  license?: License;
  // thumbnail
  thumbnailVersion: string;
  thumbnailPosition: HngVector3;
  thumbnailRotation: HngVector3;
  thumbnailScale: number;
  // filter
  filter: VaultObjectFilter;
  // annotations
  annotations: VaultObjectAnnotationDto[];
  // textureFiles
  textureFiles: File[];
}

export const VAULT_OBJECT_UPLOAD_DEFAULT_DATA: Omit<
  VaultObjectUploadData,
  "uploadType" | "uid" | "url" | "file" | "extension" | "layers"
> = {
  // metadata
  name: "",
  tags: [],
  downloadable: true,
  // thumbnail
  thumbnailVersion: THUMBNAIL_VERSION,
  thumbnailPosition: { x: 0, y: 0, z: 0 },
  thumbnailRotation: { x: 0, y: 0, z: 0 },
  thumbnailScale: 1,
  filter: FilterPreset.Default_241217,
  // annotations
  annotations: [],
  // textureFiles
  textureFiles: [],
};

export function toggleVaultObjectDetailCardViewerControls(
  currentPage:
    | "viewer"
    | "viewer-edit"
    | "viewer-thumbnail"
    | "uploader"
    | "uploader-thumbnail",
  cameraControls: HengeCameraControls,
  controls: boolean,
) {
  if (currentPage.endsWith("thumbnail")) {
    cameraControls.mouseButtons.left = CameraControlsStdlib.ACTION.NONE;
    cameraControls.touches.one = CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.right = CameraControlsStdlib.ACTION.NONE;
    cameraControls.touches.three = CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.wheel = CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.middle = CameraControlsStdlib.ACTION.NONE;
    cameraControls.touches.two = CameraControlsStdlib.ACTION.NONE;
  } else {
    cameraControls.mouseButtons.left = controls
      ? CameraControlsStdlib.ACTION.ROTATE
      : CameraControlsStdlib.ACTION.NONE;
    cameraControls.touches.one = controls
      ? CameraControlsStdlib.ACTION.TOUCH_ROTATE
      : CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.right = CameraControlsStdlib.ACTION.OFFSET;
    cameraControls.touches.three = CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.wheel = controls
      ? CameraControlsStdlib.ACTION.DOLLY
      : CameraControlsStdlib.ACTION.NONE;
    cameraControls.mouseButtons.middle = controls
      ? CameraControlsStdlib.ACTION.DOLLY
      : CameraControlsStdlib.ACTION.NONE;
    cameraControls.touches.two = controls
      ? CameraControlsStdlib.ACTION.TOUCH_DOLLY
      : CameraControlsStdlib.ACTION.NONE;
  }
}

export function updateVaultObjectViewerCamera(
  currentPage:
    | "viewer"
    | "viewer-edit"
    | "viewer-thumbnail"
    | "uploader"
    | "uploader-thumbnail",
  cameraControls: HengeCameraControls,
  enableTransition: boolean,
  fov: number,
  size: Size,
  wrapperSize: { width: number; height: number } | undefined,
  targetObject: THREE.Object3D,
  position: HngVector3 | undefined,
  rotation: HngVector3 | undefined,
  scale: number | undefined,
  extension: string,
  controls: boolean,
  captureHiddenViewer: boolean,
): Promise<void>[] {
  if (!cameraControls || !size) {
    return [
      Promise.reject(new Error("Required dependencies are not initialized")),
    ];
  }

  toggleVaultObjectDetailCardViewerControls(
    currentPage,
    cameraControls,
    controls,
  );

  cameraControls.smoothTime = SMOOTH_TIME;
  cameraControls.draggingSmoothTime = DRAGGING_SMOOTH_TIME;

  cameraControls.dollySpeed = 2.0;
  cameraControls.truckSpeed = 3.0;
  cameraControls.polarRotateSpeed = 1.5;
  cameraControls.azimuthRotateSpeed = 1.5;

  cameraControls.minPolarAngle = 0;
  cameraControls.maxPolarAngle = Math.PI;
  cameraControls.minAzimuthAngle = -Infinity;
  cameraControls.maxAzimuthAngle = Infinity;

  //================================================================

  const promises: Promise<void>[] = [];

  (cameraControls.camera as THREE.PerspectiveCamera).fov = fov;
  cameraControls.camera.updateProjectionMatrix();

  const targetObjectWorldPosition = new THREE.Vector3();
  targetObject.getWorldPosition(targetObjectWorldPosition);

  const boundingBox = targetObject.userData.boundingBox;
  let heightWidthRatio = 1;
  if (boundingBox) {
    const xy = Math.pow(
      (boundingBox.max.x - boundingBox.min.x) ** 2 +
        (boundingBox.max.y - boundingBox.min.y) ** 2,
      0.5,
    );
    const yz = Math.pow(
      (boundingBox.max.y - boundingBox.min.y) ** 2 +
        (boundingBox.max.z - boundingBox.min.z) ** 2,
      0.5,
    );
    const zx = Math.pow(
      (boundingBox.max.z - boundingBox.min.z) ** 2 +
        (boundingBox.max.x - boundingBox.min.x) ** 2,
      0.5,
    );
    heightWidthRatio = Math.max(Math.max(Math.max(xy, yz), zx));
  }

  /* Target */
  const target = {
    x: 0,
    y: 0,
    z: 0,
  };
  if (currentPage.endsWith("thumbnail")) {
    if (captureHiddenViewer) {
      target.y = HENGE_SCALE_CONSTANT * 1.6;
    } else {
      target.y = (45.2 / VAULT_OBJECT_THUMBNAIL_ZOOM_CONSTANT) * 0.9375;
    }
  } else {
    target.x = targetObjectWorldPosition.x;
    target.y = targetObjectWorldPosition.y;
    target.z = targetObjectWorldPosition.z;

    if (boundingBox) {
      target.y += (boundingBox.max.y - boundingBox.min.y) * -0.1;
    }
  }

  promises.push(
    cameraControls.setTarget(target.x, target.y, target.z, enableTransition),
  );

  /* Zoom & Distance */
  const distanceRatio =
    Math.tan(((GAIA_FOV / 2) * Math.PI) / 180) /
    Math.tan(((fov / 2) * Math.PI) / 180);
  const cameraDistance = GAIA_CAMERA_DISTANCE * distanceRatio;

  // cameraControls.minDistance =
  //   (cameraDistance * VAULT_OBJECT_MIN_DISTANCE_CONSTANT) /
  //   Math.max(1, heightWidthRatio);
  // cameraControls.maxDistance =
  //   (cameraDistance * VAULT_OBJECT_MAX_DISTANCE_CONSTANT) /
  //   Math.max(1, heightWidthRatio);

  let gaiaZoom = calcZoom(size);
  if (currentPage.endsWith("thumbnail") && wrapperSize) {
    gaiaZoom =
      GAIA_ZOOM_CONSTANT * VAULT_OBJECT_THUMBNAIL_ZOOM_CONSTANT * 1.175;
  }

  let hengeZoom = gaiaZoom * HENGE_ORTHOGRAPHIC_ZOOM_CONSTANT;
  if (currentPage.endsWith("thumbnail")) {
    if (captureHiddenViewer) {
      hengeZoom *= 0.92;
    } else {
      hengeZoom *= 0.8;
    }
  } else {
    hengeZoom *= 0.54;
    hengeZoom /= heightWidthRatio;
  }
  if (scale) hengeZoom *= scale;

  // if (OBJECT3D.SPLAT.includes(extension.toLowerCase())) {
  //   promises.push(cameraControls.zoomTo(hengeZoom / 20, enableTransition));
  //   promises.push(
  //     cameraControls.dollyTo(cameraDistance / 20, enableTransition),
  //   );
  // } else {
  promises.push(cameraControls.zoomTo(hengeZoom, enableTransition));
  promises.push(cameraControls.dollyTo(cameraDistance, enableTransition));
  // }

  /* FocalOffset */
  promises.push(cameraControls.setFocalOffset(0, 0, 0, enableTransition));

  /* Rotate */
  promises.push(
    cameraControls.rotateTo(
      DEFAULT_AZIMUTH_ANGLE,
      DEFAULT_POLAR_ANGLE,
      enableTransition,
    ),
  );

  return promises;
}

export function splitItems({
  total = 0,
  maxPerRow,
  canUpload,
}: {
  total?: number;
  maxPerRow: number;
  canUpload?: boolean;
}) {
  const result: number[][] = [[]];

  let currentRow = 0;
  // let currentCol = 0;
  let currentMaxItemsPerRow = 1;

  if (canUpload) {
    result[currentRow] = [NaN];
    currentRow++;
    currentMaxItemsPerRow++;
  }

  for (let i = 0; i < total; ) {
    if (result[currentRow] === undefined) {
      result[currentRow] = [];
    }

    // result[currentRow].push(items[i]);

    // row 당 item 개수 증가 구간
    if (currentRow <= maxPerRow - 1) {
      for (let j = 0; j < currentMaxItemsPerRow; j++) {
        // currentCol++;
        result[currentRow].push(1);
        i++;
      }
      currentRow++;
      currentMaxItemsPerRow++;
    }
    // 진동 구간
    else {
      // 양쪽 빈 슬폿 필요
      const needSideDummy = (maxPerRow + currentRow - 1) % 2 === 1;

      if (needSideDummy) {
        // currentCol++;
        result[currentRow].push(-1);
      }
      for (let j = 0; j < maxPerRow - ((maxPerRow + currentRow - 1) % 2); j++) {
        // currentCol++;
        result[currentRow].push(1);
        i++;
      }
      if (needSideDummy) {
        // currentCol++;
        result[currentRow].push(-1);
      }
      currentRow++;
    }
  }

  return result;
}
